import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Robot, Routine } from '@sb/remote-control/types';
import { getRoutine, onGetRobot, onGetRoutine } from '@sbrc/services';

import { getPrefetchedRoutine } from './usePrefetchRoutines';
import { useRouter } from './useRouter';

/**
 * Get robot and routine data for page.
 * The robotID may be in the URL. If it isn't then get first robot.
 * The routineID may be in the URL. If it isn't then get the routine linked to the robot.
 * When `robot`/`routine` is `null`, it means the data doesn't exist.
 * When `robot`/`routine` is `undefined`, it means the request is still loading.
 */
export default function usePageRobotAndRoutine(): {
  robot: Robot.ConvertedResponse | null | undefined;
  routine: Routine.ConvertedResponse | null | undefined;
  /**
   * Re-read the routine and replace the page's copy with what is stored now.
   *
   * The subscription only moves when the service emits an event, and a write
   * the service refuses emits none. An editor whose write was refused as stale
   * would otherwise keep the copy it read, and every later write from it would
   * be refused the same way until something else touched the routine.
   */
  refreshRoutine: () => Promise<void>;
} {
  const { query } = useRouter();

  const robotID = typeof query.robotID === 'string' ? query.robotID : undefined;

  const [robot, setRobot] = useState<
    Robot.ConvertedResponse | null | undefined
  >();

  const [routine, setRoutine] = useState<
    Routine.ConvertedResponse | null | undefined
  >(() => {
    // Seed from the prefetch cache so navigating from the routine list renders
    // instantly instead of flashing a spinner.
    const initialID = Array.isArray(query.routineID)
      ? query.routineID[0]
      : query.routineID;

    return initialID ? getPrefetchedRoutine(initialID) : undefined;
  });

  // The latest snapshot DELIVERED by the subscription, kept for deduping
  // without taking `robot` as an effect dependency (which would re-subscribe).
  // Written only by the subscription callback — mirroring the committed
  // `robot` here on every render would reset the baseline to pre-snapshot
  // state whenever an unrelated render interleaved with an uncommitted
  // update, making the dedupe check swallow later snapshots that matched the
  // stale committed state.
  const robotRef = useRef<Robot.ConvertedResponse | null | undefined>(robot);

  useEffect(() => {
    const checkedSetRobot = (robotToCheck: Robot.ConvertedResponse | null) => {
      if (!robotToCheck) {
        robotRef.current = null;
        setRobot(null);

        return;
      }

      const prevRobot = robotRef.current;

      if (isEqual(prevRobot, robotToCheck)) {
        return;
      }

      robotRef.current = robotToCheck;

      // Every delivery commits urgently, on purpose. Pure jog flips
      // (Idle ↔ RunningAdHocCommand at every press/release) are suppressed
      // upstream by the `onGetRobots` subscription (see JOG_STATE_KINDS), so
      // a snapshot that reaches this callback is a rare, meaningful
      // transition — run started/stopped, failure, pause. Deferring it (e.g.
      // via startTransition) would let sustained urgent render traffic
      // (e.g. streaming equipment state) starve the transition lane
      // indefinitely, leaving the routine page showing the idle UI through
      // an entire routine run.
      setRobot(robotToCheck);
    };

    return onGetRobot(robotID, checkedSetRobot);
  }, [robotID]);

  const waitingForRobot = robot === undefined;

  const routineID =
    (Array.isArray(query.routineID) ? query.routineID[0] : query.routineID) ??
    robot?.latestRoutineID;

  // Reset routine state the instant the id changes — synchronously, during
  // render. Otherwise a `null` resolved for a prior id leaks into the next
  // id's first committed frame and bounces the editor page to the routine
  // list: while loading, the app briefly routes through a param-less path
  // (e.g. `/routines`) where the hook falls back to `robot.latestRoutineID`,
  // a different routine that may resolve to `null`. Doing this in an effect
  // would still paint one stale frame before the effect runs; setting state
  // during render (React's documented "adjust state on input change" pattern)
  // re-renders before painting, so the stale value never reaches the DOM.
  // Seed from the prefetch cache so the navigate-from-list case stays instant.
  const [dataRoutineID, setDataRoutineID] = useState(routineID);

  if (routineID !== dataRoutineID) {
    setDataRoutineID(routineID);
    setRoutine(routineID ? getPrefetchedRoutine(routineID) : null);
  }

  // get the routine
  useEffect(() => {
    if (waitingForRobot && !routineID) {
      return undefined;
    }

    if (!routineID) {
      setRoutine(null);

      return undefined;
    }

    const unsubscribe = onGetRoutine(routineID, setRoutine);

    return () => unsubscribe();
  }, [waitingForRobot, routineID]);

  const refreshRoutine = useCallback(async () => {
    if (!routineID) return;

    const fresh = await getRoutine(routineID);

    // A missing routine is the subscription's call to make (it also handles
    // the redirect); a refresh only ever replaces a copy with a newer one.
    if (fresh !== null) {
      setRoutine(fresh);
    }
  }, [routineID]);

  return {
    robot,
    routine,
    refreshRoutine,
  };
}
